import React, { useLayoutEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { a, useSpring, easings } from "@react-spring/three";

const degToRad = (d: number) => (d * Math.PI) / 180;

/** Animate from [0, 0, 0] → [90°, 180°, 0] (in radians). */
const INITIAL_ROTATION = [degToRad(115), degToRad(200), degToRad(58)];
const FINAL_ROTATION = [degToRad(90), degToRad(180), 0];

/**
 * Animated directional + ambient light that fades up in intensity.
 */
function AnimatedLights() {
  const { dirBrightness, dirPosition, ambBrightness } = useSpring({
    from: { dirBrightness: 0, dirPosition: [10, -20, 5], ambBrightness: 0 },
    to: { dirBrightness: 10, dirPosition: [-30, 20, 30], ambBrightness: 0.2 },
    config: {
      duration: 2000,
      easing: easings.easeInOutCubic,
    },
  });

  return (
    <>
      {/* 'a' stands for animated. We can animate intensity with it */}
      <a.directionalLight color="#394459" position={dirPosition} intensity={dirBrightness} />
      <a.ambientLight intensity={ambBrightness} color={"#ddd"} />
    </>
  );
}

function AnimatedMesh({
  geometry,
  initialRotation = INITIAL_ROTATION,
  initialScale = 0.3,
}: {
  geometry: any;
  initialRotation?: number[];
  initialScale?: number;
}) {
  // Animate rotation and scale
  const { rotation, scale } = useSpring({
    from: { rotation: initialRotation, scale: initialScale },
    to: { rotation: FINAL_ROTATION, scale: 0.5 },
    config: {
      duration: 2000,
      easing: easings.easeInOutCubic,
    },
  });

  return (
    <a.mesh scale={scale} rotation={rotation} geometry={geometry} castShadow receiveShadow>
      <a.meshStandardMaterial roughness={1.0} metalness={0.0} transparent={true} />
    </a.mesh>
  );
}

/**
 * Main component
 */
export default function Splash() {
  const { nodes: innerNodes } = useGLTF("/3d/Inner.gltf");
  const { nodes: outerNodes } = useGLTF("/3d/Outer.gltf");
  const [unmount, setUnmount] = React.useState(false);

  useLayoutEffect(() => {
    // Skip splash animation if window.__SKIP_SPLASH__ is set (e.g., in tests)
    if ((window as any).__SKIP_SPLASH__) {
      setUnmount(true);
      return;
    }

    setTimeout(() => {
      setUnmount(true);
    }, 3.3 * 1000);
  }, []);

  if (unmount) {
    return null;
  }

  return (
    <>
      <div className="splash-blocker" />

      <div className={`fixed z-30 h-screen w-screen *-pointer-events-none logoFadeInOut`}>
        <Canvas
          camera={{ position: [0, 0, 3000], near: 0.1, far: 5000 }}
          onCreated={({ camera }) => camera.setFocalLength(500)}
          className="absolute inset-0 z-20 pointer-events-none"
        >
          <AnimatedLights />

          <AnimatedMesh geometry={innerNodes.mesh_0.geometry} initialScale={0.2} />
          <AnimatedMesh
            geometry={outerNodes.mesh_0.geometry}
            initialRotation={[degToRad(-150), degToRad(350), degToRad(90)]}
          />
        </Canvas>
      </div>
    </>
  );
}
